import Axios, { AxiosRequestConfig, AxiosInstance } from 'axios'
import { History } from 'history'
import { v4 as uuidv4 } from 'uuid'

import { AlertLevels, ConfigType } from 'types'
import { ApiInterface } from 'types/api'
import { AuthenticatorInterface } from 'types/authenticator'

export const getRequestConfig = (error) => {
  let requestConfig = { ...error.config, baseURL: '' }
  if (
    error.config.data &&
    (typeof error.config.data === 'string' ||
      error.config.data instanceof String)
  ) {
    let data
    try {
      data = JSON.parse(error.config.data)
    } catch (e) {
      data = error.config.data
    }
    requestConfig = { ...requestConfig, data }
  }
  return requestConfig
}

export default class Api implements ApiInterface {
  config
  history
  authenticator
  axios: AxiosInstance
  axiosRetry
  token

  constructor(
    config: ConfigType,
    history: History,
    authenticator: AuthenticatorInterface
  ) {
    this.config = config
    this.history = history
    this.authenticator = authenticator

    this.axios = Axios.create({
      baseURL: config.api,
    })
    this.axiosRetry = Axios.create({
      baseURL: config.api,
    })

    this.axios.interceptors.request.use((request) =>
      requestInterceptor(request)
    )
    // Ingen response hantering för denna
    this.axiosRetry.interceptors.request.use((request) =>
      requestInterceptor(request)
    )

    const requestInterceptor = (request) => {
      // Add breadcrumb ID header for API call tracing
      request.headers.breadcrumbId = uuidv4()
      // Add authorization header with secure token
      const token = this.authenticator.getToken()
      if (token) {
        request.headers.Authorization = `Bearer ${token.access_token}`
      }
      return request
    }

    this.axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Intercept errors
        if (error.request) {
          switch (error.request.status) {
            // 401 Unauthorized, attempt to refresh token and retry original request
            case 401:
              return this.authenticator
                .updateToken()
                .then(() => {
                  const requestConfig = getRequestConfig(error)
                  return this.axiosRetry(requestConfig)
                    .then((response) => {
                      return response
                    })
                    .catch((error) => {
                      if (error.request.status === 401) {
                        return Promise.reject('ACCESS_DENIED')
                      }
                      if (error.request.status === 403) {
                        this.history.push(this.config.locations.accessDenied)
                        return Promise.reject('ACCESS_DENIED')
                      }
                      if (error.request.status === 404) {
                        this.history.push(this.config.locations.pageNotFound)
                        return Promise.reject('PAGE_NOT_FOUND')
                      }
                      if (
                        error.request.status === 400 &&
                        error.response.request.response.errorCode
                      ) {
                        return Promise.reject(
                          error.response.request.response.errorCode
                        )
                      }
                      return Promise.reject(error)
                    })
                })
                .catch((error) => {
                  if (typeof error === 'string') {
                    return Promise.reject(error)
                  }
                  // Log out on failed refresh
                  this.history.push(this.config.locations.loggedOut.pathname)
                  return Promise.reject('TOKEN_EXPIRED')
                })
            // 403 Forbidden, redirect to error page
            case 403:
              this.history.push(this.config.locations.accessDenied)
              return Promise.reject('ACCESS_DENIED')
          }
        }
        return Promise.reject(error)
      }
    )
  }

  // API call methods

  /**
   * Make an api call
   */
  request(call: AxiosRequestConfig, data = null, preserveHeaders = false) {
    // Apply default values and extract call parameters
    let {
      method,
      url,
      headers,
      multipart,
      responseType,
      transformResponse,
      ...options
    } = {
      method: 'get',
      headers: {},
      multipart: false,
      responseType: 'json',
      data,
      ...call,
    }
    let config: AxiosRequestConfig

    if (multipart) {
      headers['Content-Type'] = 'multipart/form-data'
      data = this.toFormData(data)
    }

    // Add JSON response check for IE11
    if (responseType === 'json') {
      if (transformResponse) {
        // Add before other transforms
        transformResponse = [
          this.toJSON,
          ...(Array.isArray(transformResponse)
            ? transformResponse
            : [transformResponse]),
        ]
      } else {
        transformResponse = this.toJSON
      }
    }

    config = {
      method,
      headers,
      url,
      data,
      responseType,
      transformResponse,
      ...options,
    }

    return this.axios
      .request(config)
      .then((response) => {
        if (preserveHeaders) {
          return response
        }
        return response.data
      })
      .catch((requestError) => this.handleError(requestError, call, data))
  }

  /**
   * Handle basic errors
   */
  async handleError(error, call, data) {
    // Default error handling
    let errorMessage = 'UNKNOWN_ERROR'
    if (error) {
      if (error.response) {
        if (
          error.response.status === 400 &&
          error.response.request.response.errorCode
        ) {
          errorMessage = error.response.request.response.errorCode
          // Server error
        } else if (
          error.response.status >= 500 &&
          error.response.status < 600
        ) {
          errorMessage = 'SERVER_ERROR'
          // API error code
        } else if (error.response.data && error.response.data.errorCode) {
          errorMessage = error.response.data.errorCode
        }
      } else {
        // Axios timeout error
        if (error.code && error.code === 'ECONNABORTED') {
          errorMessage = 'NETWORK_TIMEOUT'
        }
        // Network error
        if (error.message && error.message === 'Network Error') {
          errorMessage = 'NETWORK_ERROR'
        }
        // Pass through errors
        if (
          typeof error === 'string' &&
          [
            'TOKEN_EXPIRED',
            'ACCESS_DENIED',
            'PAGE_NOT_FOUND',
            'PERSON_ALREADY_ORG_ADMIN',
            'PERSON_ALREADY_PURCHASER',
            'PERSON_ALREADY_ACCOUNT_ADMIN',
            'PERSON_ALREADY_INVOICE_RECIPIENT_ADMIN',
            'PERSON_ALREADY_ORG_ADMIN',
            'PARAMETER_VALIDATION_ERROR',
            'APPLICATION_IN_PROGRESS',
            'ORGANIZATION_ALREADY_CUSTOMER',
          ].includes(error)
        ) {
          errorMessage = error
        }
      }
    }
    return Promise.reject({
      level: AlertLevels.ERROR,
      message: errorMessage,
      data: error,
    })
  }

  toJSON(data) {
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data)
      } catch {}
    }
    return data
  }

  /**
   * Converts a plain object map to a FormData object
   * Only uses formData.append for IE11 compatibility
   */
  toFormData(data) {
    if (data instanceof FormData) return data
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof File) {
        formData.append(key, value as File, value.name)
      } else {
        formData.append(key, value as any)
      }
    })
    return formData
  }

  // Keycloak methods

  getLoginUrl(clientId) {
    return this.authenticator.getLoginUrl(clientId)
  }

  authenticateUser(params) {
    return this.authenticator.authenticateUser(params).catch((error) =>
      Promise.reject({
        level: AlertLevels.ERROR,
        message: error,
      })
    )
  }

  logout(redirectUrl?: string) {
    return this.authenticator.logout(redirectUrl)
  }
}
